<template>
  <AppLayout :title="`${stripHtml(product.name)} - SONNENGLAS Produktdetails`">
    <div class="min-h-screen bg-white">
      <!-- Main Content -->
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Back to Catalog Navigation -->
        <div class="mb-8">
          <Link 
            :href="route('products')"
            class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            {{ $t('Zurück zum Katalog') }}
          </Link>
        </div>

        <!-- Product Detail Container -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Product Images with Gallery -->
          <div class="space-y-4">
            <!-- Main Image Display -->
            <div class="bg-gray-50 rounded-lg p-8 flex items-center justify-center min-h-[400px]">
              <img
                :src="getCurrentImage()"
                :alt="product.name"
                class="max-w-full max-h-full object-contain"
                @error="handleImageError"
              />
            </div>

            <!-- Image Thumbnails (if gallery exists) -->
            <div v-if="imageGallery.length > 1" class="flex gap-2 overflow-x-auto">
              <button
                v-for="(img, index) in imageGallery"
                :key="index"
                @click="currentImageIndex = index"
                :class="[
                  'flex-shrink-0 w-20 h-20 rounded border-2 overflow-hidden transition-all',
                  currentImageIndex === index ? 'border-sonnenglas-600' : 'border-gray-300 hover:border-gray-400'
                ]"
              >
                <img
                  :src="img"
                  :alt="`${product.name} - Image ${index + 1}`"
                  class="w-full h-full object-cover"
                />
              </button>
            </div>

            <!-- Long Description (below photos) -->
            <div v-if="localizedDescription" class="bg-gray-50 p-6 rounded-lg">
              <div class="text-gray-700 leading-relaxed prose max-w-none" v-html="localizedDescription">
              </div>
            </div>
          </div>

          <!-- Product Information -->
          <div class="space-y-6">
            <!-- Product Name -->
            <div>
              <h1 class="text-3xl font-bold text-gray-900 mb-2" v-html="localizedName">
              </h1>
              <p class="text-sm text-gray-500">
                SKU: {{ product.sku }}
              </p>
            </div>

            <!-- Short Description (prominent) -->
            <div v-if="localizedShortDescription" class="bg-sonnenglas-50 border-l-4 border-sonnenglas-400 p-4">
              <p class="text-lg text-gray-800 font-medium">
                {{ localizedShortDescription }}
              </p>
            </div>

            <!-- Pricing Information -->
            <div class="bg-gray-50 p-6 rounded-lg">
              <!-- Recommended Retail Price. Hidden for POS materials, which have no UVP (feedback #07c). -->
              <div v-if="!product.isPos" class="mb-6">
                <p class="text-sm text-gray-600 mb-1">{{ $t('Unverbindliche Preisempfehlung') }}</p>
                <p class="text-2xl font-bold text-gray-900">
                  {{ formatPrice(product.price.value) }}
                </p>
              </div>

              <!-- Pricing tiers, or a single fixed price when identical across all tiers (feedback #07d) -->
              <div v-if="!isFixedPriceProduct">
                <div class="flex items-center gap-1.5 mb-3">
                  <p class="text-sm text-gray-600">{{ $t('Ihre Einkaufspreise (nach Preisstufen)') }}</p>
                  <TierInfoTooltip />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div 
                    v-for="tier in sortedPricingTiers" 
                    :key="tier.name"
                    class="relative bg-white border border-gray-200 rounded-lg p-4 hover:border-sonnenglas-300 transition-colors group"
                    :title="`${$t('Ab')} ${formatPrice(tier.revenueThreshold)} ${$t('Gesamtbestellwert')}`"
                  >
                    <!-- Tier Name and Discount -->
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-gray-700">{{ tier.name }}</span>
                      <span class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        -{{ tier.discount }}%
                      </span>
                    </div>
                    
                    <!-- Price -->
                    <div class="text-lg font-bold text-sonnenglas-600">
                      {{ formatPrice(tier.price) }}
                    </div>
                    
                    <!-- Minimum Order Value -->
                    <div class="text-xs text-gray-500 mt-1">
                      {{ $t('Ab') }} {{ formatPrice(tier.revenueThreshold) }}
                    </div>

                    <!-- Tooltip -->
                    <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      {{ $t('Gesamtbestellwert') }}: {{ formatPrice(tier.revenueThreshold) }}
                      <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Fixed purchase price: tiers hidden because the price is identical in every tier (feedback #07d) -->
              <div v-else>
                <p class="text-sm text-gray-600 mb-1">{{ $t('Ihr Einkaufspreis') }}</p>
                <p class="text-2xl font-bold text-sonnenglas-600">{{ formatPrice(fixedPriceValue) }}</p>
              </div>
            </div>


            <!-- Technical Specifications -->
            <div v-if="hasTechnicalData()" class="bg-gray-50 p-6 rounded-lg">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Technische Daten') }}</h3>
              <div class="grid grid-cols-1 gap-4 text-sm">
                <div v-if="product.weight && product.weight > 0">
                  <span class="text-gray-600">{{ $t('Gewicht') }}:</span>
                  <span class="ml-2 font-medium">{{ product.weight }}g</span>
                </div>
                <div v-if="getDimensionsText()">
                  <span class="text-gray-600">{{ $t('Abmessungen') }}:</span>
                  <span class="ml-2 font-medium">{{ getDimensionsText() }}</span>
                </div>
                <div v-if="shouldShowCartonQuantity(product.cartonQuantity)">
                  <span class="text-gray-600">{{ $t('Kartoninhalt') }}:</span>
                  <span class="ml-2 font-medium">{{ getCartonQuantity(product.cartonQuantity) }} {{ $t('Stück/Karton') }}</span>
                </div>
              </div>
            </div>


            <!-- Stock Status with color-coded dot and delivery time -->
            <div class="flex items-center text-sm text-gray-700">
              <div :class="statusColor" class="w-2 h-2 rounded-full mr-2"></div>
              <template v-if="isDiscontinued">
                <span class="text-gray-500">{{ $t('Nicht mehr verfügbar') }}</span>
              </template>
              <template v-else-if="isSoldOut">
                <span class="text-red-600 font-medium">{{ $t('Ausverkauft') }}</span>
              </template>
              <template v-else-if="isInStock">
                <template v-if="hasAvailableQuantity">
                  {{ $t(':available sofort verfügbar', { available: availableQuantity }) }}
                </template>
                <template v-else>
                  {{ $t('Sofort verfügbar') }}
                </template>
              </template>
              <template v-else-if="isPreOrder">
                <span class="text-red-800 dark:text-red-700">
                  {{ $t('Vorbestellung möglich') }} - {{ $t('Lieferung') }} {{ deliveryWeeks }} {{ isMonths ? (deliveryWeeks.includes('-') || parseInt(deliveryWeeks.replace('ca ', '')) > 1 ? $t('Monate') : $t('Monat')) : (deliveryWeeks.includes('-') || parseInt(deliveryWeeks) > 1 ? $t('Wochen') : $t('Woche')) }}
                </span>
              </template>
              <template v-else>
                {{ $t('Lieferung') }} {{ deliveryWeeks }} {{ isMonths ? (deliveryWeeks.includes('-') || parseInt(deliveryWeeks.replace('ca ', '')) > 1 ? $t('Monate') : $t('Monat')) : (deliveryWeeks.includes('-') || parseInt(deliveryWeeks) > 1 ? $t('Wochen') : $t('Woche')) }}
              </template>
            </div>

            <!-- Add to Cart Section -->
            <div class="bg-white border border-gray-200 p-6 rounded-lg">
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <label for="quantity" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ $t('Menge') }}
                  </label>
                  <select
                    id="quantity"
                    v-model="selectedQuantity"
                    class="block w-20 text-sm border border-gray-300 rounded px-3 py-2 bg-white text-center focus:border-gray-400 focus:outline-none"
                  >
                    <option v-for="qty in availableQuantities" :key="qty" :value="qty">
                      {{ qty }}
                    </option>
                  </select>
                </div>

                <div class="flex-grow">
                  <button
                    @click="addToCart"
                    :disabled="isLoading || isDiscontinued || isSoldOut"
                    class="btn btn-sonnenglas w-full py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed justify-center"
                  >
                    <svg
                      v-if="isLoading"
                      class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <svg v-else class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                    </svg>
                    {{ $t('In den Warenkorb') }}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Cart Sidebar -->
      <CartSidebar />

      <!-- Add to Cart Modal -->
      <AddToCartModal
        :is-open="showAddToCartModal"
        :product-name="stripHtml(localizedName)"
        :quantity="selectedQuantity"
        @close="showAddToCartModal = false"
      />
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { useCartStore } from '@/stores/cart'
import AppLayout from '@/layouts/AppLayout.vue'
import CartSidebar from '@/components/CartSidebar.vue'
import AddToCartModal from '@/components/AddToCartModal.vue'
import TierInfoTooltip from '@/components/TierInfoTooltip.vue'
import type { Product } from '@/types/product'
import { useQuantityOptions } from '@/composables/useQuantityOptions'
import { useDeliveryStatus } from '@/composables/useDeliveryStatus'
import { getCartonQuantity, shouldShowCartonQuantity } from '@/utils/cartonQuantities'
import { getProductName, getProductShortDescription, getProductDescription } from '@/utils/localization'

// Inject the route function
const route = inject<(name?: string, params?: any, absolute?: boolean) => string>('route')

// Helper function to strip HTML tags for page title
const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, (entity) => {
    const entities: Record<string, string> = {
      '&reg;': '®',
      '&trade;': '™',
      '&copy;': '©',
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'"
    }
    return entities[entity] || entity
  })
}

// Props passed from Inertia (ProductsController)
interface PricingTier {
  name: string
  discount: number
  revenueThreshold: number
  currency: string
  price: number
}

interface Props {
  product: Product
  currency: string
  quantities: Record<string, number>
  pricingTiers: PricingTier[]
  cartProducts?: Product[] // Products currently in cart
}

const props = defineProps<Props>()

const cartStore = useCartStore()
const { addToCart: addToCartStore } = cartStore

const selectedQuantity = ref(1)
const isLoading = ref(false)
const currentImageIndex = ref(0)
const showAddToCartModal = ref(false)

// Build image gallery from API images and local fallbacks
const imageGallery = computed(() => {
  const images: string[] = []

  // PRIORITY 1: Xhosa API main image
  if (props.product.images?.main) {
    images.push(props.product.images.main)
  }

  // PRIORITY 2: Xhosa API gallery images
  if (props.product.images?.gallery && Array.isArray(props.product.images.gallery)) {
    images.push(...props.product.images.gallery)
  }

  // PRIORITY 3: Local images as fallback (if no API images)
  if (images.length === 0) {
    const localMainImage = `/images/products/${props.product.sku.toLowerCase()}.jpg`
    images.push(localMainImage)
  }

  return images
})

const getCurrentImage = (): string => {
  return imageGallery.value[currentImageIndex.value] || imageGallery.value[0]
}

// Initialize/update cart store for this product
onMounted(() => {
  // Transform pricing tiers to match cart store format
  const transformedTiers = props.pricingTiers.map(tier => ({
    id: tier.id || 1, // Fallback in case id is missing
    name: tier.name,
    discount: tier.discount,
    revenueThreshold: tier.revenueThreshold,
    currency: tier.currency
  }))

  // Create product tier pricing structure for this single product
  const productTierPricing: Record<string, Record<string, number>> = {}
  productTierPricing[props.product.sku] = {}

  // Extract tier prices from the pricingTiers array
  props.pricingTiers.forEach((tier) => {
    const tierKey = `tier${tier.id || 1}`
    productTierPricing[props.product.sku][tierKey] = tier.price
  })

  // CRITICAL FIX: Initialize cart with ALL cart products, not just current product
  // This ensures cart sidebar shows all items, not just the current product
  const allProductsForCart = props.cartProducts && props.cartProducts.length > 0
    ? [...props.cartProducts, props.product] // Include cart products + current product
    : [props.product] // Fallback to just current product if no cart

  // Remove duplicates (in case current product is also in cart)
  const uniqueProducts = allProductsForCart.filter((product, index, self) =>
    index === self.findIndex((p) => p.sku === product.sku)
  )

  if (cartStore.allProducts.length === 0) {
    // First time initialization with all cart products
    cartStore.initializeCart(uniqueProducts, props.quantities, transformedTiers, productTierPricing, props.currency)
  } else {
    // Cart already exists - ensure all cart products are available
    uniqueProducts.forEach(product => {
      const productExists = cartStore.allProducts.some(p => p.sku === product.sku)
      if (!productExists) {
        cartStore.addProduct(product)
      }
    })
    // Update pricing info for this product
    cartStore.updateProductPricing(props.product.sku, productTierPricing[props.product.sku])

    // CRITICAL: Sync quantities from backend (props.quantities contains the real server cart state)
    cartStore.syncQuantitiesFromBackend(props.quantities)
  }
})

// Localized computed properties using utility functions
const localizedName = computed(() => {
  return getProductName(props.product)
})

const localizedShortDescription = computed(() => {
  return getProductShortDescription(props.product)
})

const localizedDescription = computed(() => {
  return getProductDescription(props.product)
})

// Use delivery status composable for consistent behavior across all components
const { isInStock, isDiscontinued, isSoldOut, deliveryWeeks, statusColor, isPreOrder, isMonths, availableQuantity, hasAvailableQuantity } = useDeliveryStatus(computed(() => props.product))

// Use the composable to get carton-based quantity options. POS materials are capped at 1; non-POS
// options are capped at the hard orderable limit (immediate available + usable replenishment).
const { quantityOptions } = useQuantityOptions(props.product.cartonQuantity ?? 1, props.product.isPos, props.product.maxOrderableQuantity)

const availableQuantities = computed(() => {
  // Always use carton-based quantity options regardless of stock status
  // Users should be able to order full carton quantities even for long delivery times
  return quantityOptions.value
})

const sortedPricingTiers = computed(() => {
  return [...props.pricingTiers].sort((a, b) => a.revenueThreshold - b.revenueThreshold)
})

// A product has a fixed price when it is the same across all tiers; tiers are then hidden
// and a single purchase price is shown instead (feedback #07d).
const isFixedPriceProduct = computed((): boolean => {
  const p = props.product
  // POS materials are free and have a single price, so no tiers are shown (feedback #07c).
  if (p.isPos || p.excludedFromDiscounts || p.fixedNetPrice || p.fixedNetPriceLaunch) {
    return true
  }
  const prices = props.pricingTiers
    .map((t) => t.price)
    .filter((v) => v !== undefined && v !== null && !isNaN(v))
  return prices.length > 1 && prices.every((v) => v === prices[0])
})

const fixedPriceValue = computed((): number => {
  const p = props.product
  // POS materials are free: their purchase price (EK) is always 0, never a tier/RRP value.
  if (p.isPos) return 0
  if (p.fixedNetPriceLaunch) return p.fixedNetPriceLaunch.value
  if (p.fixedNetPrice) return p.fixedNetPrice.value
  return props.pricingTiers[0]?.price ?? props.product.price.value
})

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  const currentSrc = target.src
  const sku = props.product.sku.toLowerCase()

  // Step 1: If API image failed, try local image
  if (currentSrc.includes('xhosa.sonnenglas.net')) {
    target.src = `/images/products/${sku}.jpg`
    return
  }

  // Step 2: If local full image failed, try thumbnail
  if (currentSrc.includes(`/${sku}.jpg`) && !currentSrc.includes('_thumb')) {
    target.src = `/images/products/${sku}_thumb.jpg`
    return
  }

  // Step 3: Final fallback to placeholder
  if (!currentSrc.includes('placeholder')) {
    target.src = '/images/products/placeholder.jpg'
  }
}

const getDimensionsText = (): string => {
  const dimensions = []

  if (props.product.height && props.product.height > 0) {
    dimensions.push(`H: ${props.product.height}mm`)
  }

  if (props.product.width && props.product.width > 0) {
    dimensions.push(`B: ${props.product.width}mm`)
  }

  if (props.product.length && props.product.length > 0) {
    dimensions.push(`T: ${props.product.length}mm`)
  }

  return dimensions.join(', ')
}

const hasTechnicalData = (): boolean => {
  return (
    (props.product.weight && props.product.weight > 0) ||
    getDimensionsText().length > 0 ||
    shouldShowCartonQuantity(props.product.cartonQuantity) ||
    props.product.deliveryInWeeksFrom ||
    props.product.deliveryInWeeksTo
  )
}

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: props.product.currency || props.currency || 'EUR'
  }).format(price)
}

const addToCart = async () => {
  if (props.product.hide || isDiscontinued.value || isSoldOut.value || isLoading.value) return

  isLoading.value = true
  try {
    // Add to Pinia store for UI reactivity (our updated store now handles Product objects)
    addToCartStore(props.product, selectedQuantity.value)

    // Show modal immediately for better UX
    showAddToCartModal.value = true

    // Also send to backend (happens in background)
    const quantities = {}
    quantities[props.product.sku] = selectedQuantity.value

    router.post(route?.('cart') || '/cart', {
      quantities
    }, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        // Backend confirmed - modal is already showing
      },
      onError: () => {
        // Handle error - close modal and revert store change
        showAddToCartModal.value = false
        console.error('Failed to add item to cart')
        alert(trans('Fehler beim Hinzufügen zum Warenkorb. Bitte versuchen Sie es erneut.'))
        // Revert the store change
        const currentQty = cartStore.getQuantity(props.product.sku)
        cartStore.updateQuantity(props.product.sku, Math.max(0, currentQty - selectedQuantity.value))
      }
    })
  } catch (error) {
    console.error('Error adding to cart:', error)
    showAddToCartModal.value = false
    alert(trans('Ein unerwarteter Fehler ist aufgetreten.'))
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.prose p {
  margin-bottom: 1rem;
  line-height: 1.6;
}
</style>